import {observer} from 'mobx-react-lite'
import React, {forwardRef, useRef} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Hub, Tag} from 'yougile-model'
import {Tracking, TrackingEvent} from '@common/tracking'
import {ButtonNew} from '@components/common/base/buttons'
import {Help} from '@components/common/base/help'
import {HelpKey} from '@components/common/base/help/types'
import {Icon} from '@components/common/base/icon'
import {UncontrolledSwitcher} from '@components/common/base/switcher/switcher'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {MessageBox} from '@components/message-box'
import {
  getAutomationsCount,
  hasAutomations
} from '@components/sticker/my-stickers/board-stickers/sticker-settings/utils/get-automations-count'
import {isStickerOnLastBoard} from '@components/sticker/my-stickers/my-stickers-utils'
import {getStickerTypeMessage} from '@components/sticker/my-stickers/utils'
import {updateRef} from '@containers/element-utils'
import {
  useForceUpdate,
  useSyncEvent,
  useTooltipVisible
} from '@containers/hooks'
import {Sticker} from '@extensions/sticker/sticker'
import {useMyStickersStore} from '../my-stickers-store'
import DragIcon from './drag.svg'

// todo check container size after popup refactoring and maybe change 130 below to something else
const TwStickerMainContainer = tw.div`
  flex
  flex-col
  gap-0
`

const TwSticker = tw.div`
  cursor-grab
  flex
  flex-1
  flex-col
  gap-8
  rounded-8
  bg-background-primary
  shadow-xs
  hover:shadow-sm
  hover:outline
  hover:outline-1
  hover:outline-line-hover
  group
`

const TwTitleRow = tw.div`
  px-8
  pt-12
  flex
  gap-16
  h-48
`

const TwSettingsRow = tw.div`
  p-8
  pl-48
  flex
  gap-8
  items-center
  h-48
`

const TwIconWrapper = tw.div`
  flex
  gap-4
`

const TwDragIcon = tw(DragIcon)`
  text-icon-placeholder
  invisible
  group-hover:visible
`

const TwTitleWrapper = tw.div`
  min-w-0
  flex-1
  flex
  flex-col
  gap-4
`

const TwTitleContainer = tw.div`
  min-w-0
  flex
  items-center
  gap-4
`

const TwTitle = tw.div<{$disabled?: boolean}>`
  min-w-0
  overflow-hidden
  whitespace-nowrap
  text-ellipsis
  text-sm-semibold
  max-w-130
  ${p => (p.$disabled ? 'text-secondary' : 'text-primary')}
`

export const TwTooltipText = tw.span`
  break-words
  line-clamp-3
`

const TwType = tw.div`
  text-xs-regular
  text-secondary
`

const TwAutomationInfo = tw.div<{$disabled?: boolean}>`
  whitespace-nowrap
  ml-auto
  text-xs-regular
  ${p => (p.$disabled ? 'text-secondary' : 'text-primary')}
`

const TwPlaceholder = tw.div`
  rounded-8
  bg-background-secondary
`

const Placeholder = ({height}: {height: number}) => (
  <TwPlaceholder style={{height, marginTop: -height}} />
)

const draggedStyle = {
  transition: '0.01s',
  transform: 'translateX(-9999px)'
}

const defaultHeight = 72

const getStickerTypeHelp = (sticker: Tag): HelpKey => {
  const stickerType = Sticker.getType(sticker)
  return `${stickerType}Sticker` as HelpKey
}

const Title = ({sticker, disabled}: {sticker: Tag; disabled?: boolean}) => {
  const title = Sticker.getName(sticker)
  const titleRef = useRef<HTMLDivElement>(null)
  const tooltipVisible = useTooltipVisible(titleRef, [title])

  return (
    <Tooltip
      content={<TwTooltipText>{title}</TwTooltipText>}
      disabled={!tooltipVisible}
      offset={[0, 10]}
      placement="bottom"
    >
      <TwTitle ref={titleRef} $disabled={disabled}>
        {title}
      </TwTitle>
    </Tooltip>
  )
}

export const BoardStickersItem = observer(
  forwardRef<HTMLDivElement, {sticker: Tag; board: Hub; active: boolean}>(
    ({sticker, board, active}, forwardedRef) => {
      const [state, store] = useMyStickersStore()
      const currentItemElement = useRef<HTMLDivElement>()

      const draggedSticker = sticker.id === state.draggedSticker?.id
      const draggedHeight =
        currentItemElement.current?.offsetHeight || defaultHeight

      const icon = Sticker.getIcon(sticker) || 'string'
      const stickerSettings = board.data.stickersSettings?.[sticker.id] ?? {}

      // to update data derived from board sticker settings
      const forceUpdate = useForceUpdate()
      useSyncEvent(
        'current',
        {debounceTime: 100, filter: [sticker.id, board.id]},
        forceUpdate,
        [sticker.id, board.id]
      )

      return (
        <TwStickerMainContainer
          ref={el => {
            currentItemElement.current = el
            updateRef(el, forwardedRef)
          }}
        >
          <TwSticker
            draggable
            onDragStart={() => store.dragStart(sticker, board)}
            onDragEnter={() => store.dragEnter(sticker)}
            onDragEnd={() => store.dragEnd(board)}
            style={draggedSticker ? draggedStyle : {}}
          >
            <TwTitleRow>
              <TwIconWrapper>
                <TwDragIcon />
                <Icon icon={icon} className="text-icon-secondary" />
              </TwIconWrapper>
              <TwTitleWrapper>
                <TwTitleContainer>
                  <Title sticker={sticker} disabled={!active} />
                  {Sticker.isSystem(sticker) && (
                    <Help name={getStickerTypeHelp(sticker)} />
                  )}
                  {hasAutomations(sticker) && (
                    <TwAutomationInfo $disabled={!active}>
                      {Lang.msg.myStickers.automations(
                        getAutomationsCount(sticker, stickerSettings)
                      )}
                    </TwAutomationInfo>
                  )}
                </TwTitleContainer>
                <TwType>{getStickerTypeMessage(sticker)}</TwType>
              </TwTitleWrapper>
            </TwTitleRow>
            <ActionsRow sticker={sticker} board={board} active={active} />
          </TwSticker>
          {draggedSticker && <Placeholder height={draggedHeight} />}
        </TwStickerMainContainer>
      )
    }
  )
)

const ActionsRow = observer(
  ({sticker, board, active}: {sticker: Tag; board: Hub; active: boolean}) => {
    const [, store] = useMyStickersStore()

    const isSystem = Sticker.isSystem(sticker)
    const hasSettings = Sticker.hasSettings(sticker)

    const editClick = (): void => {
      store.editSticker(sticker.id)
      void Tracking.send(
        TrackingEvent.boardSettingsOpenStickerEditorViaStickersList
      )
    }

    const deleteClick = (): void => {
      if (isStickerOnLastBoard(sticker) && !Sticker.isSystem(sticker)) {
        store.openRemoveStickerPopup(sticker)
      } else {
        Sticker.removeFromBoard(board, sticker)
        MessageBox.show(Lang.msg.myStickers.settingsApplied, 'success', {
          modern: true
        })
      }
    }

    const switcherClick = (): void => {
      Sticker.toggleActiveInBoard(board, sticker)
      MessageBox.show(Lang.msg.myStickers.settingsApplied, 'success', {
        modern: true
      })
    }

    return (
      <TwSettingsRow>
        {hasSettings && (
          <ButtonNew
            color="secondary"
            title={Lang.msg.myStickers.editSettings}
            onClick={editClick}
          />
        )}
        {!isSystem && (
          <ButtonNew
            color="secondary"
            title={Lang.msg.myStickers.removeFromBoard}
            onClick={deleteClick}
          />
        )}
        <UncontrolledSwitcher
          className="ml-auto"
          active={active}
          onClick={switcherClick}
        />
      </TwSettingsRow>
    )
  }
)
